import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { cn } from "@/lib/utils/ui";

type VersionHistoryHostContextValue = {
  container: HTMLElement | null;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isPreviewing: boolean;
  setIsPreviewing: (value: boolean) => void;
  previewLabel: string | null;
  setPreviewLabel: (value: string | null) => void;
  restore: () => void;
  registerRestore: (fn: (() => void) | null) => void;
};

const VersionHistoryHostContext =
  createContext<VersionHistoryHostContextValue | null>(null);

export function VersionHistoryHost({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewLabel, setPreviewLabel] = useState<string | null>(null);
  const restoreRef = useRef<(() => void) | null>(null);
  const restore = useCallback(() => {
    restoreRef.current?.();
  }, []);
  const registerRestore = useCallback((fn: (() => void) | null) => {
    restoreRef.current = fn;
  }, []);
  const value = useMemo(
    () => ({
      container: slot,
      open,
      setOpen,
      isPreviewing,
      setIsPreviewing,
      previewLabel,
      setPreviewLabel,
      restore,
      registerRestore,
    }),
    [slot, open, isPreviewing, previewLabel, restore, registerRestore],
  );

  return (
    <VersionHistoryHostContext.Provider value={value}>
      <div
        className={cn(
          "flex min-h-0 min-w-0 flex-1",
          open && "version-history-open",
          isPreviewing && "version-history-previewing",
        )}
      >
        {children}
        <div ref={setSlot} className="flex h-full shrink-0 empty:hidden" />
      </div>
    </VersionHistoryHostContext.Provider>
  );
}

export function useVersionHistoryHost() {
  return useContext(VersionHistoryHostContext);
}

export function useVersionHistoryOpen() {
  const host = useVersionHistoryHost();
  const [localOpen, setLocalOpen] = useState(false);
  if (host) {
    return [host.open, host.setOpen] as const;
  }
  return [localOpen, setLocalOpen] as const;
}

export function useVersionHistoryPreview() {
  return useVersionHistoryHost()?.isPreviewing ?? false;
}
